import { useState } from 'react';
import { useFlags } from 'flagsmith/react';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@pella/react-current-user';
import { useUpdateRequest, useMarkCommunicationRead, useLinkedActionCompletion } from '../../../hooks';
import { STATUSES, TRANSACTION_TYPES_SET } from '../../../data/constants';
import {
  TrailerToDockDrawer,
  IssueToLineDrawer,
  SubInventoryTransferDrawer,
  DirectOrgTransferDrawer,
  CommunicationDrawer,
} from '../Drawers';
import { ActionModal } from '../ActionModal/ActionModal';
import { LinkedActionConfirmModal } from '../../atoms';
import { getCommunicationCounts } from '../../../utils';

import { Badge, Button, toast } from '@pella/sash-framework';

import * as classes from './RequestCardButtons.module.css';

/**
 * Request Card Buttons molecule
 * @param {Local.RequestCardButtonsProps} props - Component props
 */
export const RequestCardButtons = ({ request }) => {
  const { pathname } = useLocation();
  const { userid } = useCurrentUser();
  const isWorklist = pathname === '/worklist';
  const isRequest = pathname === '/request';
  const [openTransactionDrawer, setOpenTransactionDrawer] = useState(false);
  const [openHoldModal, setOpenHoldModal] = useState(false);
  const [openCommunicationDrawer, setOpenCommunicationDrawer] = useState(false);
  const { mutate } = useUpdateRequest();
  const { mutateAsync: markAsRead } = useMarkCommunicationRead();
  const { completeWithLinkedActionCheck, isLinkedActionPromptOpen, linkedActionDescription, onDecline, onConfirm } =
    useLinkedActionCompletion();

  const { 'communication-button': communicationButton } = useFlags(['communication-button']);
  const communicationButtonEnabled = communicationButton.enabled;
  const { total, unread } = getCommunicationCounts(request, userid);
  const hasUnread = unread > 0;
  const totalBadgeLabel = hasUnread
    ? `Total communications: ${total}. Unread communications: ${unread}`
    : `Total communications: ${total}. No unread communications`;
  const totalBadgeStateClass = hasUnread ? classes.totalBadgeUnread : classes.totalBadgeNeutral;

  const communicationButtonLabel = (
    <span className={classes.communicationButtonContent}>
      <span>Communication</span>
      {total > 0 && (
        <span className={`${classes.totalBadgeCorner} ${totalBadgeStateClass}`}>
          <Badge color='neutral' variant='pill' data-testid='total-communications-badge' aria-label={totalBadgeLabel}>
            {total}
          </Badge>
        </span>
      )}
    </span>
  );

  /**
   * Handles the update request mutation.
   * @param {Pella.ProductionRequestAPI.Request['id']} id - The ID of the request to update.
   * @param {Pella.ProductionRequestAPI.Request['status']} status - The new status of the request.
   * @param {string | null} [onHoldReason] - Hold reason label, only set when placing on hold via the reason modal.
   */
  const handleUpdateRequest = (id, status, onHoldReason) => mutate({ id, status, onHoldReason });

  /**
   * Handles the "Put on Hold" action. When the request's action requires a hold reason,
   * open the reason modal instead of placing the request on hold directly.
   */
  const handlePutOnHold = () => {
    // showOnHoldReason is not declared on the shared Action type, so read it through a narrow cast.
    const requiresHoldReason = /** @type {{ showOnHoldReason?: boolean }} */ (request.action)?.showOnHoldReason;
    if (requiresHoldReason) {
      setOpenHoldModal(true);
    } else {
      handleUpdateRequest(request.id, STATUSES.ON_HOLD);
    }
  };

  /**
   * Places the request on hold with the reason selected from the modal.
   * @param {string} reason - The selected hold reason label.
   */
  const handleSelectHoldReason = (reason) => {
    handleUpdateRequest(request.id, STATUSES.ON_HOLD, reason);
    setOpenHoldModal(false);
  };

  /**
   * handles completion of a request
   * @param {Pella.ProductionRequestAPI.Request} acceptedRequest
   * @param {Pella.ProductionRequestAPI.Request['status']} status
   */
  const handleComplete = (acceptedRequest, status) => {
    if (TRANSACTION_TYPES_SET.has(acceptedRequest?.action?.transactionId)) {
      setOpenTransactionDrawer(true);
    } else {
      completeWithLinkedActionCheck(acceptedRequest, () =>
        mutate(
          { id: acceptedRequest.id, status },
          { onSuccess: () => toast.success('Request completed successfully!') },
        ),
      );
    }
  };
  /**
   * Opens the Communication drawer and marks the request's communications as read.
   * Communications are available on every request regardless of transaction type.
   * @param {Pella.ProductionRequestAPI.Request} messageRequest - The request to add a communication to.
   */
  const handleAddCommunication = async (messageRequest) => {
    // Awaited on purpose: nothing renders readBy yet, but unread counts are coming, and settling
    // the receipt before the drawer opens keeps a count from flickering read after it paints.
    // Do not swap this for a fire-and-forget mutate.
    try {
      await markAsRead(messageRequest);
    } catch {
      // mutateAsync rejects as well as invoking the hook's onError, so the rejection has to be
      // swallowed here or it surfaces as an unhandled promise rejection. The hook has already
      // toasted; the drawer opens either way so the history stays reachable.
    }

    setOpenCommunicationDrawer(true);
  };
  return (
    <>
      {request.status === STATUSES.AVAILABLE && (
        <>
          {communicationButtonEnabled && (
            <Button
              className={`${classes.button} ${classes.communicationButton}`}
              color='secondary'
              data-testid='addCommunication'
              onClick={() => handleAddCommunication(request)}
            >
              {communicationButtonLabel}
            </Button>
          )}
          {isRequest && (
            <Button
              className={classes.button}
              color='primary'
              onClick={() => handleUpdateRequest(request.id, STATUSES.RECALLED)}
            >
              Recall
            </Button>
          )}
          {isWorklist && (
            <>
              <Button className={classes.button} color='secondary' onClick={handlePutOnHold}>
                Put on Hold
              </Button>
              <Button
                className={classes.button}
                color='primary'
                onClick={() => handleUpdateRequest(request.id, STATUSES.ACCEPTED)}
              >
                Accept
              </Button>
            </>
          )}
        </>
      )}
      {request.status === STATUSES.ACCEPTED && (
        <>
          {communicationButtonEnabled && (
            <Button
              className={`${classes.button} ${classes.communicationButton}`}
              color='secondary'
              data-testid='addCommunication'
              onClick={() => handleAddCommunication(request)}
            >
              {communicationButtonLabel}
            </Button>
          )}
          {isWorklist && (
            <>
              <Button
                className={classes.button}
                color='secondary'
                onClick={() => handleUpdateRequest(request.id, STATUSES.AVAILABLE)}
              >
                Remove
              </Button>
              <Button
                className={classes.button}
                color='primary'
                onClick={() => handleComplete(request, STATUSES.COMPLETED)}
              >
                Complete
              </Button>
            </>
          )}
        </>
      )}

      {request.status === STATUSES.ON_HOLD && (
        <>
          {communicationButtonEnabled && (
            <Button
              className={`${classes.button} ${classes.communicationButton}`}
              color='secondary'
              data-testid='addCommunication'
              onClick={() => handleAddCommunication(request)}
            >
              {communicationButtonLabel}
            </Button>
          )}
          {isWorklist && (
            <Button
              className={classes.button}
              color='primary'
              onClick={() => handleUpdateRequest(request.id, STATUSES.AVAILABLE)}
            >
              Move to Available
            </Button>
          )}
        </>
      )}
      {request?.action?.transactionId === 'TRAILER_TO_DOCK' && openTransactionDrawer && (
        <TrailerToDockDrawer
          open={openTransactionDrawer}
          close={() => setOpenTransactionDrawer(false)}
          request={request}
        />
      )}
      {request?.action?.transactionId === 'ISSUE_TO_LINE' && openTransactionDrawer && (
        <IssueToLineDrawer
          open={openTransactionDrawer}
          close={() => setOpenTransactionDrawer(false)}
          request={request}
        />
      )}
      {request?.action?.transactionId === 'SUB_INVENTORY_TRANSFER' && openTransactionDrawer && (
        <SubInventoryTransferDrawer
          open={openTransactionDrawer}
          close={() => setOpenTransactionDrawer(false)}
          request={request}
        />
      )}
      {request?.action?.transactionId === 'DIRECT_ORG_TRANSFER' && openTransactionDrawer && (
        <DirectOrgTransferDrawer
          open={openTransactionDrawer}
          close={() => setOpenTransactionDrawer(false)}
          request={request}
        />
      )}
      {communicationButtonEnabled && openCommunicationDrawer && (
        <CommunicationDrawer
          open={openCommunicationDrawer}
          close={() => setOpenCommunicationDrawer(false)}
          request={request}
        />
      )}
      <ActionModal open={openHoldModal} close={() => setOpenHoldModal(false)} onSelectReason={handleSelectHoldReason} />
      <LinkedActionConfirmModal
        isOpen={isLinkedActionPromptOpen}
        description={linkedActionDescription}
        onDecline={onDecline}
        onConfirm={onConfirm}
      />
    </>
  );
};
